const MONTHS = {
  long: ['იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი', 'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი'],
  short: ['იან', 'თებ', 'მარ', 'აპრ', 'მაი', 'ივნ', 'ივლ', 'აგვ', 'სექ', 'ოქტ', 'ნოე', 'დეკ'],
} as const;

const WEEKDAYS = {
  long: ['კვირა', 'ორშაბათი', 'სამშაბათი', 'ოთხშაბათი', 'ხუთშაბათი', 'პარასკევი', 'შაბათი'],
  short: ['კვი', 'ორშ', 'სამ', 'ოთხ', 'ხუთ', 'პარ', 'შაბ'],
  narrow: ['კ', 'ო', 'ს', 'ო', 'ხ', 'პ', 'შ'],
} as const;

const ENGLISH_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface DateFormatter {
  format(date: Date | number): string;
}

/**
 * Georgian dates for a browser whose ICU has no Georgian (Chromium builds that trim locale data print
 * "Sep 25" for ka-GE). British English already orders day, month, year and uses a 24-hour clock, as
 * Georgian does, so its parts are reused with the names swapped and CLDR's punctuation for `ka`.
 * `georgian-dates.test.ts` holds this to Node's full ICU for every pattern the app uses.
 */
export function georgianDateFormat(options: Intl.DateTimeFormatOptions): DateFormatter {
  const base = new Intl.DateTimeFormat('en-GB', options);
  const resolved = base.resolvedOptions();
  const index = new Intl.DateTimeFormat('en-US', { timeZone: resolved.timeZone, month: 'numeric', weekday: 'short' });
  const monthWidth = resolved.month === 'long' || resolved.month === 'short' ? resolved.month : null;
  const weekdayWidth = resolved.weekday === 'long' || resolved.weekday === 'short' || resolved.weekday === 'narrow' ? resolved.weekday : null;
  return {
    format(date) {
      const at = index.formatToParts(date);
      const month = Number(at.find((p) => p.type === 'month')?.value) - 1;
      const weekday = ENGLISH_WEEKDAYS.indexOf(at.find((p) => p.type === 'weekday')?.value ?? '');
      const parts = base.formatToParts(date);
      return parts
        .map((part, i) => {
          const before = parts[i - 1];
          const next = parts[i + 1];
          if (part.type === 'weekday' && weekdayWidth) return WEEKDAYS[weekdayWidth][weekday];
          if (part.type === 'month' && monthWidth) return MONTHS[monthWidth][month] + (monthWidth === 'short' && parts[i + 2]?.type === 'year' ? '.' : '');
          if (part.type !== 'literal') return part.value;
          if (before?.type === 'weekday' && next) return ', ';
          if (part.value === ' at ') return ', ';
          if (before?.type === 'month' && monthWidth === 'long' && next?.type === 'year') return ', ';
          if (part.value === '/') return '.';
          return part.value;
        })
        .join('');
    },
  };
}

type RelativeUnit = 'second' | 'minute' | 'hour' | 'day';

const UNIT_WORDS: Record<RelativeUnit, { past: string; future: string; now: string }> = {
  second: { past: 'წამის წინ', future: 'წამში', now: 'ახლა' },
  minute: { past: 'წუთის წინ', future: 'წუთში', now: 'ამ წუთში' },
  hour: { past: 'საათის წინ', future: 'საათში', now: 'ამ საათში' },
  day: { past: 'დღის წინ', future: 'დღეში', now: 'დღეს' },
};

const NAMED_DAYS: Record<string, string> = { '-2': 'გუშინწინ', '-1': 'გუშინ', '1': 'ხვალ', '2': 'ზეგ' };

/** `Intl.RelativeTimeFormat('ka', { numeric: 'auto' })` for the same browsers. */
export function georgianRelativeTime(value: number, unit: RelativeUnit): string {
  const words = UNIT_WORDS[unit];
  if (value === 0) return words.now;
  const named = unit === 'day' ? NAMED_DAYS[String(value)] : undefined;
  if (named) return named;
  return value < 0 ? `${-value} ${words.past}` : `${value} ${words.future}`;
}
